import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClerkProvider, useAuth } from "@clerk/react";
import { AppLoading } from "@/components/AppState";
import { useT } from "@/lib/i18n";
import { dark } from "@clerk/ui/themes";

const appearance = {
  theme: dark,
  variables: {
    colorPrimary: "#4F46E5",
    colorPrimaryForeground: "#FFFFFF",
    colorBackground: "#111118",
    colorForeground: "#F8FAFC",
    colorInput: "#18181B",
    colorInputForeground: "#F8FAFC",
    fontFamily: "Outfit, sans-serif",
    borderRadius: "0.5rem",
  },
  captcha: { theme: "dark" },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SessionReady = ({ children }) => {
  const { isLoaded } = useAuth();
  return isLoaded ? children : <AppLoading />;
};

// Starts Clerk from the backend's runtime config, so deploy's live keys need no rebuild.
// Must render inside <BrowserRouter> (see step 3) so Clerk navigates without reloading.
export default function ClerkAuthProvider({ children }) {
  const navigate = useNavigate();
  const { t } = useT();
  const [config, setConfig] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [retry, setRetry] = useState(0);

  // Retries while the backend restarts (e.g. right after provisioning): the app's
  // Clerk components crash if they ever render without <ClerkProvider>.
  useEffect(() => {
    let cancelled = false;
    let timer;
    const controller = new AbortController();
    const deadline = setTimeout(() => { controller.abort(); if (!cancelled) setUnavailable(true); }, 15000);
    setUnavailable(false);
    const load = async (attempt = 0) => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/clerk/config`, { signal: controller.signal });
        if (!response.ok) throw new Error("Config unavailable");
        const cfg = await response.json();
        if (cfg.enabled && cfg.publishable_key) {
          clearTimeout(deadline);
          if (!cancelled) setConfig(cfg);
          return;
        }
      } catch {}
      if (cancelled || controller.signal.aborted) return;
      if (attempt >= 3) { clearTimeout(deadline); setUnavailable(true); return; }
      timer = setTimeout(() => load(attempt + 1), 1200);
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); clearTimeout(deadline); controller.abort(); };
  }, [retry]);

  if (unavailable) return <div role="alert" data-testid="auth-config-error" className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 text-slate-100"><p>{t("auth_unavailable")}</p><button data-testid="auth-config-retry" className="bg-indigo-600 rounded-full px-5 py-2" onClick={() => setRetry((n) => n + 1)}>{t("retry")}</button></div>;
  if (!config) return <AppLoading />;
  return (
    <ClerkProvider
      publishableKey={config.publishable_key}
      proxyUrl={config.proxy_url || undefined}
      appearance={appearance}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
      signInForceRedirectUrl="/dashboard"
      signUpForceRedirectUrl="/dashboard"
      afterSignOutUrl="/"
    >
      <SessionReady>{children}</SessionReady>
    </ClerkProvider>
  );
}
